/**
 * Hook personalizado para manejar el estado del tutorial
 * Proporciona una interfaz simple para verificar y controlar el tutorial
 */

import { useState, useEffect } from 'react';
import { configService } from '../services/ConfigService';

export interface TutorialState {
  hasSeenTutorial: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface TutorialActions {
  markTutorialAsSeen: () => Promise<void>;
  resetTutorial: () => Promise<void>;
  refreshTutorialState: () => Promise<void>;
}

export interface UseTutorialReturn extends TutorialState, TutorialActions {}

/**
 * Hook para manejar el estado del tutorial
 * 
 * @example
 * ```typescript
 * const { hasSeenTutorial, isLoading, markTutorialAsSeen } = useTutorial();
 * 
 * if (isLoading) return <IonSpinner />;
 * 
 * if (!hasSeenTutorial) {
 *   return <Tutorial onComplete={markTutorialAsSeen} />;
 * }
 * 
 * return <MainApp />;
 * ```
 */
export const useTutorial = (): UseTutorialReturn => {
  const [state, setState] = useState<TutorialState>({
    hasSeenTutorial: false,
    isLoading: true,
    error: null
  });

  /**
   * Carga el estado inicial del tutorial
   */
  const loadTutorialState = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const hasSeenTutorial = await configService.hasSeenTutorial();
      
      setState(prev => ({
        ...prev,
        hasSeenTutorial,
        isLoading: false
      }));
      
      console.log('📚 useTutorial: Estado cargado -', { hasSeenTutorial });
    } catch (error) {
      console.error('📚 useTutorial: Error al cargar estado:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error desconocido'
      }));
    }
  };

  /**
   * Marca el tutorial como visto
   */
  const markTutorialAsSeen = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await configService.setTutorialSeen(true);
      
      setState(prev => ({
        ...prev,
        hasSeenTutorial: true,
        isLoading: false
      }));
      
      console.log('📚 useTutorial: Tutorial marcado como visto');
    } catch (error) {
      console.error('📚 useTutorial: Error al marcar tutorial:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error al marcar tutorial'
      }));
    }
  };

  /**
   * Resetea el estado del tutorial
   */
  const resetTutorial = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await configService.resetTutorial();
      
      setState(prev => ({
        ...prev,
        hasSeenTutorial: false,
        isLoading: false
      }));
      
      console.log('📚 useTutorial: Tutorial reseteado');
    } catch (error) {
      console.error('📚 useTutorial: Error al resetear tutorial:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error al resetear tutorial'
      }));
    }
  };

  /**
   * Refresca el estado del tutorial desde el storage
   */
  const refreshTutorialState = async () => {
    await loadTutorialState();
  };

  // Cargar estado inicial al montar el componente
  useEffect(() => {
    loadTutorialState();
  }, []);

  return {
    // Estado
    hasSeenTutorial: state.hasSeenTutorial,
    isLoading: state.isLoading,
    error: state.error,
    
    // Acciones
    markTutorialAsSeen,
    resetTutorial,
    refreshTutorialState
  };
};

/**
 * Hook simplificado que solo verifica si se ha visto el tutorial
 * Útil cuando solo necesitas verificar el estado sin modificarlo
 */
export const useHasSeenTutorial = (): { hasSeenTutorial: boolean; isLoading: boolean } => {
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkTutorial = async () => {
      try {
        const seen = await configService.hasSeenTutorial();
        setHasSeenTutorial(seen);
      } catch (error) {
        console.error('Error al verificar tutorial:', error);
        setHasSeenTutorial(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkTutorial();
  }, []);

  return { hasSeenTutorial, isLoading };
};

export default useTutorial;
